import type { ElplanBidDayTableBody } from 'api/elplan/elplan.types'
import { readDayAheadBidName } from 'helpers/elplan.helper/bid.helper'
import { readDayAheadBidValue } from 'helpers/elplan.helper/bidValue.helper'
import type { RowValues } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import Datetime from 'utils/datetime/datetime'
import { getDayAheadBidCellKey } from './DayAheadBidTable.grid.helper'
import type { DayAheadBidColumn, DayAheadBidRow } from './DayAheadBidTable.types'

type BuildDayAheadBidDayTableBodyInput = {
  valuesByRow: RowValues[]
  columns: DayAheadBidColumn[]
  rows: DayAheadBidRow[]
  elplanInfoId: number
  date: string
  partitionId?: number
}

function parseDayAheadBidValue(rawValue: string | undefined, tagKey: string, rowKind: string): number | null {
  const reading = readDayAheadBidValue(rawValue)
  if (reading.kind === 'fault') {
    throw new Error(`Invalid numeric value for ${tagKey} ${rowKind}: ${rawValue}`)
  }

  return reading.kind === 'blank' ? null : reading.value
}

export function buildDayAheadBidDayTableBody({
  valuesByRow,
  columns,
  rows,
  elplanInfoId,
  date,
  partitionId,
}: BuildDayAheadBidDayTableBodyInput): ElplanBidDayTableBody {
  const priceRowIndex = rows.findIndex((row) => row.kind === 'price')
  const nameRowIndex = rows.findIndex((row) => row.kind === 'name')
  const hourRows = rows.filter((row): row is Extract<DayAheadBidRow, { kind: 'hour' }> => row.kind === 'hour')

  if (hourRows.some((row) => Datetime.toLocalTime(row.startTime, 'onlyDate') !== date)) {
    throw new Error(`Day table hour does not belong to ${date}`)
  }

  const body: ElplanBidDayTableBody = {
    elplan_info: elplanInfoId,
    date,
    columns: columns.map((column) => {
      const price = parseDayAheadBidValue(
        priceRowIndex >= 0
          ? valuesByRow[priceRowIndex]?.[getDayAheadBidCellKey(priceRowIndex, column.tagId)]
          : undefined,
        column.tagKey,
        'price'
      )
      const name =
        nameRowIndex >= 0
          ? readDayAheadBidName(valuesByRow[nameRowIndex]?.[getDayAheadBidCellKey(nameRowIndex, column.tagId)])
          : undefined

      return {
        tag: column.tagId,
        ...(nameRowIndex >= 0 ? { name } : {}),
        price,
        volumes: hourRows.map((row) => {
          const rowIndex = rows.indexOf(row)
          return {
            start_time: Datetime.toISOString(row.startTime),
            end_time: Datetime.toISOString(row.endTime),
            volume: parseDayAheadBidValue(
              valuesByRow[rowIndex]?.[getDayAheadBidCellKey(rowIndex, column.tagId)],
              column.tagKey,
              row.startTime
            ),
          }
        }),
      }
    }),
  }

  if (partitionId !== undefined) {
    body.partition = partitionId
  }

  return body
}
